import { Button, Flex, Typography } from 'antd';
import { PackageIcon, TruckIcon } from '@phosphor-icons/react';
import {
  overdueCount,
  ordersDestination,
  pickupsDestination,
  type Briefing,
  type FacilityBacklog,
} from './hubBriefingRules';

const MAX_ROWS = 5;

type Props = {
  briefing: Briefing;
  canOpenPackages: boolean;
  onStart: (backlog: FacilityBacklog, url: string) => void;
  /** Facilities listed before "+N more" (the alerts drawer has room for more). */
  maxRows?: number;
};

const Urgency = ({
  total,
  overdue,
  dueToday,
}: {
  total: number;
  overdue: number;
  dueToday: number;
}) => (
  <>
    {overdue > 0 && (
      <Typography.Text type="danger"> · {overdueCount(total, overdue)} overdue</Typography.Text>
    )}
    {dueToday > 0 && <Typography.Text type="secondary"> · {dueToday} due today</Typography.Text>}
  </>
);

const Line = ({
  icon,
  label,
  total,
  overdue,
  dueToday,
  action,
  ariaLabel,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  total: number;
  overdue: number;
  dueToday: number;
  action: string;
  ariaLabel: string;
  onClick: () => void;
}) => (
  <Flex align="center" justify="space-between" gap={8}>
    <Flex align="center" gap={8} style={{ minWidth: 0 }}>
      {icon}
      <Typography.Text>
        {label}
        <Urgency total={total} overdue={overdue} dueToday={dueToday} />
      </Typography.Text>
    </Flex>
    <Button
      type="link"
      size="small"
      aria-label={ariaLabel}
      onClick={onClick}
      style={{ paddingInline: 0, flexShrink: 0 }}
    >
      {action}
    </Button>
  </Flex>
);

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/**
 * What is waiting, per facility, with a link to start on it (the alerts drawer's Waiting tab).
 * Calm by design: the red alarm stays reserved for a new order that needs someone now.
 */
export const HubBriefingCard = ({
  briefing,
  canOpenPackages,
  onStart,
  maxRows = MAX_ROWS,
}: Props) => {
  const several = briefing.facilities.length > 1;
  const action = canOpenPackages ? 'Start' : 'Picklists';
  return (
    <Flex vertical gap={12} style={{ marginTop: 4 }}>
      {briefing.facilities.slice(0, maxRows).map(backlog => (
        <Flex key={backlog.facilityId} vertical gap={2}>
          {several && <Typography.Text strong>{backlog.facilityName}</Typography.Text>}
          {backlog.orders > 0 && (
            <Line
              icon={<PackageIcon size={16} />}
              label={plural(backlog.orders, 'online order', 'online orders')}
              total={backlog.orders}
              overdue={backlog.ordersOverdue}
              dueToday={backlog.ordersDueToday}
              action={action}
              ariaLabel={`${canOpenPackages ? 'Start' : 'Open picklists for'} online orders at ${backlog.facilityName}`}
              onClick={() => onStart(backlog, ordersDestination(canOpenPackages))}
            />
          )}
          {backlog.pickups > 0 && (
            <Line
              icon={<TruckIcon size={16} />}
              label={plural(backlog.pickups, 'Try at Home pickup', 'Try at Home pickups')}
              total={backlog.pickups}
              overdue={backlog.pickupsOverdue}
              dueToday={backlog.pickupsDueToday}
              action={action}
              ariaLabel={`${canOpenPackages ? 'Start' : 'Open picklists for'} Try at Home pickups at ${backlog.facilityName}`}
              onClick={() => onStart(backlog, pickupsDestination(backlog, canOpenPackages))}
            />
          )}
        </Flex>
      ))}
      {(briefing.facilities.length > maxRows || briefing.partial) && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {briefing.facilities.length > maxRows &&
            `+${briefing.facilities.length - maxRows} more ${
              briefing.facilities.length - maxRows === 1 ? 'facility' : 'facilities'
            }. `}
          {briefing.partial && 'Counts cover the first 50 of each list.'}
        </Typography.Text>
      )}
    </Flex>
  );
};
